import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Heart, Sparkles, Eye, MessageCircle, Star, Gift, Check } from 'lucide-react';
import { apiClient } from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';

const KIND_META = {
  like:           { Icon: Heart,          color: 'text-fnl-pink',     verb: 'liked your profile' },
  match:          { Icon: Sparkles,       color: 'text-fuchsia-400',  verb: "it's a match!" },
  super_like:     { Icon: Star,           color: 'text-amber-400',    verb: 'super-liked you' },
  message:        { Icon: MessageCircle,  color: 'text-sky-400',      verb: 'sent you a message' },
  view:           { Icon: Eye,            color: 'text-purple-300',   verb: 'viewed your profile' },
  referral_credit:{ Icon: Gift,           color: 'text-emerald-400',  verb: 'joined via your invite' },
  ai_match:       { Icon: Sparkles,       color: 'text-fnl-pink',     verb: 'is your match of the day' },
};

const targetFor = (n) => {
  if (n.kind === 'message') return n.actor_id ? `/chat/${n.actor_id}` : '/chat';
  if (n.kind === 'view') return '/views';
  if (n.kind === 'referral_credit') return '/me';
  if (n.kind === 'ai_match') return '/browse';
  return n.actor_id ? `/profile/${n.actor_id}` : '/browse';
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return d.toLocaleDateString();
};

const NotificationItem = ({ n, onClick }) => {
  const meta = KIND_META[n.kind] || { Icon: Bell, color: 'text-purple-300', verb: n.kind };
  const Icon = meta.Icon;
  return (
    <Link
      to={targetFor(n)}
      onClick={onClick}
      data-testid={`notif-item-${n.id}`}
      className={`flex items-start gap-3 px-3 py-2.5 rounded-lg transition-colors ${n.read ? 'hover:bg-fnl-purple-light/30' : 'bg-fnl-pink/5 hover:bg-fnl-pink/10'}`}
    >
      <div className="relative shrink-0">
        {n.actor_photo ? (
          <img src={n.actor_photo} alt={n.actor_name || ''} className="w-10 h-10 rounded-full object-cover border border-purple-800/60" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-fnl-purple-light/60 flex items-center justify-center">
            <Bell className="w-4 h-4 text-purple-300" />
          </div>
        )}
        <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-fnl-dark border-2 border-fnl-purple flex items-center justify-center ${meta.color}`}>
          <Icon className="w-2.5 h-2.5" />
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-white leading-snug">
          <span className="font-semibold">{n.actor_name || 'Someone'}</span>{' '}
          <span className="text-purple-200">{meta.verb}</span>
        </div>
        {n.kind === 'message' && n.payload?.preview && (
          <div className="text-xs text-purple-300 truncate mt-0.5">"{n.payload.preview}"</div>
        )}
        {n.kind === 'ai_match' && n.payload?.reason && (
          <div className="text-xs text-purple-300 line-clamp-2 mt-0.5">{n.payload.reason}</div>
        )}
        <div className="text-[10px] text-purple-400 mt-0.5 uppercase tracking-wider font-semibold">
          {timeAgo(n.created_at)}
        </div>
      </div>
      {!n.read && <span className="w-2 h-2 rounded-full bg-fnl-pink mt-1.5 shrink-0" />}
    </Link>
  );
};

const NotificationsBell = () => {
  const { user, wsSubscribe } = useAuth();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastFetchRef = useRef(0);

  // Fetch unread count on mount + every 60s as a safety net
  const fetchCount = useCallback(async () => {
    try {
      const r = await apiClient.get('/notifications/unread-count');
      setCount(r.data?.count || 0);
    } catch (e) { /* silent */ }
  }, []);

  const fetchList = useCallback(async () => {
    if (Date.now() - lastFetchRef.current < 500) return;
    lastFetchRef.current = Date.now();
    setLoading(true);
    try {
      const r = await apiClient.get('/notifications', { params: { limit: 20 } });
      setItems(r.data || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchCount();
    const id = setInterval(fetchCount, 60_000);
    return () => clearInterval(id);
  }, [user, fetchCount]);

  // Live updates via the shared WebSocket
  useEffect(() => {
    if (!user) return;
    const unsub = wsSubscribe((data) => {
      if (data?.type === 'notification') {
        const incoming = data.notification;
        setCount((c) => c + 1);
        setItems((prev) => {
          const without = prev.filter((p) => p.id !== incoming.id);
          return [incoming, ...without].slice(0, 20);
        });
      }
    });
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, wsSubscribe]);

  const handleOpenChange = (next) => {
    setOpen(next);
    if (next) {
      fetchList();
    }
  };

  const markAllRead = async () => {
    try {
      await apiClient.post('/notifications/mark-read', {});
      setCount(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (e) { /* silent */ }
  };

  const onItemClick = () => {
    setOpen(false);
    if (count > 0) markAllRead();
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="relative text-white hover:text-fnl-pink hover:bg-white/5"
          data-testid="nav-notifications"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
          {count > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 bg-fnl-pink text-white text-[10px] leading-none rounded-full px-1.5 py-0.5 font-bold min-w-[18px] text-center"
              data-testid="notifications-badge"
            >
              {count > 99 ? '99+' : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] sm:w-[420px] bg-fnl-purple border-purple-900 text-white p-0 overflow-hidden"
        data-testid="notifications-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-purple-900/60">
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4 text-fnl-pink" />
            <span className="font-semibold">Notifications</span>
          </div>
          {count > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="text-xs text-fnl-pink hover:text-fnl-pink-light flex items-center gap-1"
              data-testid="notifications-mark-all-read"
            >
              <Check className="w-3.5 h-3.5" />Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[440px] overflow-y-auto p-2" data-testid="notifications-list">
          {loading && items.length === 0 ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-fnl-pink border-t-transparent rounded-full animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 px-6" data-testid="notifications-empty">
              <Bell className="w-10 h-10 text-fnl-pink/40 mx-auto mb-3" />
              <div className="text-white font-semibold text-sm">You're all caught up</div>
              <p className="text-purple-300 text-xs mt-1">New likes, matches, and messages will land here.</p>
            </div>
          ) : (
            items.map((n) => (
              <NotificationItem key={n.id} n={n} onClick={onItemClick} />
            ))
          )}
        </div>
        <div className="border-t border-purple-900/60 px-4 py-2.5 text-center">
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="text-xs text-fnl-pink hover:underline font-semibold uppercase tracking-wider"
            data-testid="notifications-see-all"
          >
            See all notifications →
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationsBell;
